import * as React from 'react';

import { cn } from '../../lib/utils';
import { Tooltip, type TooltipProps } from '../Tooltip';

export interface DisabledWithReasonProps {
  /**
   * Why the control is unavailable, e.g. "Requires Super Admin". Shown on hover and keyboard focus.
   * `null` (or `''`) when the control is usable: the child then renders untouched, so callers
   * wrap unconditionally.
   */
  reason: string | null;
  placement?: TooltipProps['placement'];
  /** Story / dev only — pins the tooltip open (Tooltip's `forceHoverOpen`). */
  forceHoverOpen?: boolean;
  /**
   * Exactly one control that forwards `disabled` and `className` to its DOM element
   * (e.g. `Button`, `IconButton`).
   */
  children: React.ReactElement<{
    id?: string;
    disabled?: boolean;
    className?: string;
    tabIndex?: number;
    'aria-disabled'?: boolean;
  }>;
}

/**
 * Renders an unavailable control disabled — never hidden — with the reason on hover and
 * keyboard focus. A disabled button fires no mouse events and leaves the tab order, so a
 * focusable wrapper owns both, and the child gets `pointer-events-none` to hand it the hover.
 */
export const DisabledWithReason: React.FC<DisabledWithReasonProps> = ({
  reason,
  placement = 'top',
  forceHoverOpen,
  children,
}) => {
  // Hooks run before the early return so a reason that appears or clears keeps hook order.
  // Hover and focus are tracked apart: leaving one must not hide the reason the other still shows.
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const tooltipId = React.useId();
  const fallbackChildId = React.useId();
  if (!reason) {
    // The wrapper unmounts without a mouseleave/blur, so reset here or a returning reason opens by itself.
    if (hovered) setHovered(false);
    if (focused) setFocused(false);
    return children;
  }

  const open = hovered || focused || forceHoverOpen === true;
  const childId = children.props.id ?? fallbackChildId;
  return (
    <span
      className="relative inline-flex rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      tabIndex={0}
      // Focus lands here, not on the child, so the wrapper carries the child's name (an icon-only
      // child has only its aria-label) and disabled state. `group`, not `button`: a button may not
      // contain the child button.
      role="group"
      aria-labelledby={childId}
      aria-disabled
      // Tooltip renders nothing while hidden, so only reference it while open.
      aria-describedby={open ? tooltipId : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // A click on an unavailable control must not focus the wrapper, or the reason sticks after the pointer leaves.
      onMouseDown={(e) => e.preventDefault()}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {React.cloneElement(children, {
        id: childId,
        disabled: true,
        'aria-disabled': true,
        // An anchor child (`Button asChild`) ignores `disabled`; keep it off the keyboard too.
        tabIndex: -1,
        className: cn(children.props.className, 'pointer-events-none'),
      })}
      <Tooltip id={tooltipId} label={reason} show={open} placement={placement} />
    </span>
  );
};

DisabledWithReason.displayName = 'DisabledWithReason';
